import React, { useEffect, useState } from 'react';
import {
  Typography,
  Link as MuiLink,
  Avatar,
  ListItem,
  ListItemAvatar,
  CircularProgress,
} from '@mui/material';
import {
  NotificationMenu,
  NotificationListItem,
  NotificationLink,
  ListItemData,
  NoNotification,
} from './styles/NotificationListModal.styled';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from '@/services';
import { Notification } from '@/types';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/stores';
import { markAsRead, setNotificationCount } from '@/stores/notificationSlice';
import { getFormattedDateTime } from '@/utils/helpers';
import { useAuth } from '@/hooks/useAuth';
import { resolveNotificationTarget } from '@/utils/notificationTarget';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: (
    event: {},
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => void;
}

const NotificationListModal: React.FC<Props> = ({
  anchorEl,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useAuth();
  const open = Boolean(anchorEl);

  // Local state for fetched notifications and total count
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [_, setTotal] = useState<number>(0);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const getNotificationVisual = (note: Notification) => {
    const entity = note.entityType?.toLowerCase();
    const type = note.type?.toLowerCase();
    if (type === 'ticket_assigned' || type === 'ticket_reassigned' || entity === 'ticket') {
      return { icon: '/images/notification-icon2.svg', bg: '#0b5ed7' };
    }
    if (type === 'deal_assigned' || type === 'deal_created' || entity === 'deal') {
      return { icon: '/images/notification-icon2.svg', bg: '#198754' };
    }
    if (type === 'shift_swap_request' || type === 'shift_swap_approved' || type === 'shift_swap_rejected' || entity === 'shift_swap_request') {
      return { icon: '/images/notification-icon2.svg', bg: '#6f42c1' };
    }
    if (type === 'leave_request' || type === 'leave_request_approved' || type === 'leave_request_rejected' || entity === 'leave_request') {
      return { icon: '/images/notification-icon2.svg', bg: '#fd7e14' };
    }
    if (type === 'sales_inquiry_created' || type === 'query_inquiry_created' || type === 'customer_update_request' || entity === 'inquiry') {
      return { icon: '/images/notification-icon2.svg', bg: '#0aa2c0' };
    }
    if (type === 'invoice_success' || type === 'grace_period_start' || type === 'trial_reminder' || entity === 'subscriptioninvoice' || entity === 'usersubscription') {
      return { icon: '/images/notification-icon2.svg', bg: '#d63384' };
    }
    return { icon: '/images/notification-icon2.svg', bg: '#002f73' };
  };

  // Fetch last 10 notifications when modal opens
  useEffect(() => {
    if (!open) return;

    NotificationService.getList({ page: 1, limit: 10 })
      .then((res) => {
        const payload = res?.data?.data ? res.data : (res?.data ?? res);
        const items = payload?.data ?? [];
        const t = payload?.total ?? 0;
        setNotifications(items);
        setTotal(t);
      })
      .catch((err) =>
        console.error('Modal notifications load error:', err)
      )
  }, [open]);

  // Handle click on a single notification
  const handleItemClick = (note: Notification) => {
    dispatch(markAsRead(note.id));
    onClose({}, 'backdropClick');       // close modal
    const target = resolveNotificationTarget(note, user?.userType, user?.memberType);
    // Always navigate (even to the current route) so the destination page can
    // react via location.key — e.g. open its details modal for the same page.
    navigate(target.to, { state: target.state });
  };

  // Handle View All button
  const handleViewAll = async () => {
    if (markingAllRead) return;
    console.log('[notifications] View All clicked');
    setMarkingAllRead(true);
    try {
      await NotificationService.markAllAsRead();
      dispatch(setNotificationCount(0));
      setNotifications((prev) => prev.map((note) => ({ ...note, isRead: 1 })));
    } catch (err) {
      console.error('Mark all as read failed:', err);
    } finally {
      setMarkingAllRead(false);
      onClose({}, 'backdropClick');
      navigate('/notifications');
    }
  };

  return (
    <NotificationMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* <List sx={{ p: 0, minWidth: 300 }}> */}
      {notifications.map((note) => (
        <NotificationListItem key={note.id}>
          {(() => {
            const visual = getNotificationVisual(note);
            const title = note.title ?? 'Notification';
            const message = note.message ?? '';
            return (
          <NotificationLink
            className={note.isRead ? 'read' : ''}
            style={{ ['--notif-avatar-bg' as any]: visual.bg }}
            onClick={() => handleItemClick(note)}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: visual.bg }}>
                <img
                  src={visual.icon}
                  alt="bell"
                />
              </Avatar>
            </ListItemAvatar>
            <ListItemData>
              <Typography variant="h6">{title}</Typography>
              <Typography>{message}</Typography>
              <Typography component="span">
                {getFormattedDateTime(
                  typeof note.createdAt === 'string' ? new Date(note.createdAt) : note.createdAt
                )}
              </Typography>
            </ListItemData>
          </NotificationLink>
            );
          })()}
        </NotificationListItem>
      ))}

      {/* Show View All only if more than 10 */}
      {notifications.length > 0 &&
        <ListItem
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1.5,
          }}
        >
          <MuiLink
            component="button"
            disabled={markingAllRead}
            sx={{
              alignItems: 'center',
              background: 'none',
              border: 0,
              display: 'inline-flex',
              fontSize: 14,
              fontWeight: 600,
              gap: 1,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={handleViewAll}
          >
            {markingAllRead ? <CircularProgress color="inherit" size={16} /> : null}
            {markingAllRead ? 'Marking as read...' : 'View All'}
          </MuiLink>
        </ListItem>
      }
      {notifications.length === 0 && (
        <NoNotification>
          <img src="/images/notification-off-icon2.svg" alt="bell" />
          No Notification
        </NoNotification>
      )}
      {/* </List> */}

    </NotificationMenu>
  );
};

export default NotificationListModal;
